import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react'; // Assuming other imports exist
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Container, Alert, Spinner } from 'react-bootstrap';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { Helmet } from 'react-helmet-async';
import { FaCheck, FaRobot, FaChartLine, FaGraduationCap, FaUsers, FaCreditCard, FaInfoCircle, FaRegCalendarCheck, FaRegComments, FaRocket, FaBook, FaComments, FaTools, FaUserGraduate, FaHeadset, FaShieldAlt, FaPython, FaCheckCircle, FaTimes } from 'react-icons/fa';
// UNIFIED AUTH: Use unified authentication hook instead of direct Auth0
import useUser from '../../hooks/useUser';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import InfoModal from '../../components/InfoModal';
import { usePaymentReuse } from '../../hooks/usePaymentReuse';
import planFeaturesData from '../../data/planFeatures.json';
import ProPageContent from './ProPageContent';
import ProCta from './ProCta';
import './UpgradeToProPage.css';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';

// --- Icon Mapping ---
const iconMap = {
  FaRobot: <FaRobot />,
  FaChartLine: <FaChartLine />,
  FaGraduationCap: <FaGraduationCap />,
  FaUsers: <FaUsers />,
  FaCreditCard: <FaCreditCard />,
  FaTimes: <FaTimes className="text-danger" />, // This might be used by PlanComparisonSection, keep for now or ensure it's imported there if needed.
  FaCheck: <FaCheck className="text-success" />, // This might be used by PlanComparisonSection, keep for now or ensure it's imported there if needed.
  FaInfoCircle: <FaInfoCircle />,
  FaRegCalendarCheck: <FaRegCalendarCheck />, // Ensure this is mapped
  FaRegComments: <FaRegComments />,
  FaRocket: <FaRocket />,
  FaBook: <FaBook />,
  FaComments: <FaComments />,
  FaTools: <FaTools />,
  FaUserGraduate: <FaUserGraduate />,
  FaHeadset: <FaHeadset />,
  FaShieldAlt: <FaShieldAlt />,
  FaPython: <FaPython />
};

// --- Generate Comparison Features List from JSON ---
// This is used by ProPageContent, so it's fine here or could be moved if preferred, but it's data generation.
const comparisonFeaturesList =
  Array.isArray(planFeaturesData?.featureDefinitions) &&
  Array.isArray(planFeaturesData?.plans?.starter?.features) &&
  Array.isArray(planFeaturesData?.plans?.pro?.features)
    ? (planFeaturesData.featureDefinitions
        .filter(def => def.displayInComparison)
        .map(def => {
          // Defensive: ensure .features is an array before .find
          const starterFeatures = Array.isArray(planFeaturesData.plans.starter.features)
            ? planFeaturesData.plans.starter.features
            : [];
          const proFeatures = Array.isArray(planFeaturesData.plans.pro.features)
            ? planFeaturesData.plans.pro.features
            : [];
          // Defensive: ensure def is not null/undefined and has an id
          if (!def || typeof def.id === 'undefined') return null;
          const starterFeature = starterFeatures.find(f => f && f.id === def.id);
          const proFeature = proFeatures.find(f => f && f.id === def.id);

          const starterValue = starterFeature ? starterFeature.value : 'N/A';
          const proValue = proFeature ? proFeature.value : 'N/A';

          return {
            feature: def.name,
            starter: starterValue === "No" ? iconMap.FaTimes : (starterValue === "Yes" ? iconMap.FaCheck : starterValue),
            pro: proValue === "No" ? iconMap.FaTimes : (proValue === "Yes" ? iconMap.FaCheck : proValue),
            icon: def.icon && iconMap[def.icon] ? iconMap[def.icon] : null,
          };
        })
        .filter(Boolean)) // filter out any nulls
    : [];

// --- Generate Detailed Pro Features from JSON ---
const detailedProFeatures = Array.isArray(planFeaturesData?.plans?.pro?.features) && Array.isArray(planFeaturesData?.featureDefinitions)
  ? planFeaturesData.plans.pro.features
      .filter(pf => pf.displayInProList)
      .map(proFeature => {
        const def = planFeaturesData.featureDefinitions.find(fd => fd.id === proFeature.id);
        if (!def) return null;
        return {
          icon: def.icon && iconMap[def.icon] ? iconMap[def.icon] : '✨', // Fallback icon
          title: def.proListTitle || def.name,
          description: def.proListDescriptionTemplate 
            ? def.proListDescriptionTemplate.replace('{{value}}', proFeature.value) 
            : proFeature.value,
        };
      })
      .filter(Boolean)
  : [];


const benefitsData = [
    {
      icon: <FaRobot className="feature-icon" />,
      title: "Unleash AI Power",
      description: "Generate sophisticated trading bots in minutes. No coding, just your strategy in plain English, amplified by Pro AI capabilities."
    },
    {
      icon: <FaChartLine className="feature-icon" />,
      title: "Access Elite Strategies",
      description: "Instantly use a library of 50+ proven, Pro-level algorithms for diverse markets. Continuously updated and expanded."
    },
    {
      icon: <FaGraduationCap className="feature-icon" />,
      title: "Expert Mentorship",
      description: "Accelerate your learning with Live Algo Lab, weekly implementation help, and personalized 1-on-1 guidance from our top experts."
    },
    {
      icon: <FaUsers className="feature-icon" />,
      title: "Join the Pro Circle",
      description: "Network with serious traders, get priority support, and gain early access to new features within our exclusive Pro community."
    }
];

const valueItems = planFeaturesData.proPlanValueItems; // Use data from JSON
const totalRetailValue = valueItems.reduce((sum, item) => sum + parseFloat(item.value.replace('$', '').replace(',', '')), 0);


// --- Updated Testimonials ---
const testimonialsPro = [
  {
    quote: "The Tuesday Live-Build community took my rough ideas and turned them into five working bots in one weekend. Incredible hands-on support.",
    author: "Andrew M.",
    role: "Pro Plan Member, 2024",
    img: "/img/pro-testimonials/andrew.png"
  },
  {
    quote: "Absolutely would recommend the classes. The trading strategies I learned paid for the course itself.*",
    author: "Santayan P.",
    role: "Trustpilot Reviewer, 2022",
    img: "/img/pro-testimonials/santayan.png"
  },
  {
    quote: "Live sessions made algo trading finally click. I went from zero Python to running back-tests I can trust — and I'm still learning every week.",
    author: "Marcus B.",
    role: "Pro Plan Member, 2024",
    img: "/img/pro-testimonials/marcus.png"
  }
];

const trustBadgesData = [
  { value: "5,000+", label: "Pro Bots Deployed" },
  { value: "98%", label: "Pro Satisfaction" },
  { value: "Top 1%", label: "Trader Community" }
];

// 1. Add Schwab logo to brokers
const logoImages = [
  { src: "/images/logos/interactive-brokers.png", alt: "Interactive Brokers", width: "150", height: "32" }, 
  { src: "/images/logos/alpaca.png", alt: "Alpaca", width: "100", height: "32" }, 
  { src: "/images/logos/tradier.png", alt: "Tradier", width: "120", height: "32" }, 
  { src: "/images/logos/coinbase.png", alt: "Coinbase Pro", width: "130", height: "32" }, 
  { src: "/images/logos/binance.png", alt: "Binance", width: "120", height: "32" },
  { src: "/images/logos/schwab.png", alt: "Charles Schwab", width: "120", height: "32" },
  { src: "/images/logos/bitunix.png", alt: "BitUnix", width: "120", height: "32" },
  { src: "/images/logos/kraken.png", alt: "Kraken", width: "120", height: "32" },
  { src: "/images/logos/kucoin.png", alt: "Kucoin", width: "120", height: "32" },

];

// --- ENVIRONMENT CONSTANTS ---
const API_URL = process.env.REACT_APP_API_URL || 'https://api.botspot.trade'; // fallback optional

function UpgradeToProPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // UNIFIED AUTH: Get authentication state and API token access from unified hook
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('t');
  const fromStarterSignup = searchParams.get('from_starter_signup') === 'true';
  const emailFromQuery = searchParams.get('email');
  const customerIdFromQuery = searchParams.get('customerId');
  const paymentMethodIdFromQuery = searchParams.get('paymentMethodId');
  const last4FromQuery = searchParams.get('last4');
  const sourceFlowFromQuery = searchParams.get('source_flow');

  // 🚨 FIX: Detect Stripe success redirects and redirect to survey
  const paymentIntentFromUrl = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');
  const isStripeSuccessRedirect = paymentIntentFromUrl && redirectStatus === 'succeeded';

  // 🚨 FIX: Redirect to survey if this is a successful Stripe redirect
  useEffect(() => {
    if (isStripeSuccessRedirect && emailFromQuery) {
      console.log('[UpgradeToProPage] Detected Stripe success redirect, redirecting to survey');
      
      const surveyParams = new URLSearchParams();
      surveyParams.append('email', emailFromQuery);
      if (searchParams.get('name')) surveyParams.append('name', searchParams.get('name'));
      if (customerIdFromQuery) surveyParams.append('customerId', customerIdFromQuery);
      surveyParams.append('purchase_success', 'true');
      surveyParams.append('source_flow', 'stripe_success_redirect');
      surveyParams.append('plan_type', 'pro-plan');
      surveyParams.append('payment_intent', paymentIntentFromUrl);
      
      // Use replace to avoid back button issues
      navigate(`/signup-survey?${surveyParams.toString()}`, { replace: true });
      return;
    }
  }, [isStripeSuccessRedirect, emailFromQuery, customerIdFromQuery, paymentIntentFromUrl, searchParams, navigate]);

  // 🚨 FIX: Check if authenticated user is already Pro and redirect appropriately
  useEffect(() => {
    if (isAuthenticated && user && !authLoading && !isStripeSuccessRedirect) {
      // Check if user has Pro subscription
      const hasProSubscription = user['https://botspot.trade/subscription_status'] === 'active' && 
                                 user['https://botspot.trade/current_plan'] === 'pro-plan';
      
      if (hasProSubscription) {
        console.log('[UpgradeToProPage] User is already Pro subscriber, redirecting to onboarding');
        
        // User is already Pro - redirect to onboarding/dashboard
        const surveyParams = new URLSearchParams();
        if (user.email) surveyParams.append('email', user.email);
        if (user.name && user.name !== 'New User') surveyParams.append('name', user.name);
        if (user['https://botspot.trade/stripe_customer_id']) {
          surveyParams.append('customerId', user['https://botspot.trade/stripe_customer_id']);
        }
        surveyParams.append('purchase_success', 'true');
        surveyParams.append('source_flow', 'already_pro_redirect');
        surveyParams.append('plan_type', 'pro-plan');
        
        // Check if this looks like they just completed a payment (has payment-related params)
        const hasPaymentParams = paymentIntentFromUrl || customerIdFromQuery || emailFromQuery;
        if (hasPaymentParams) {
          // Likely just completed payment, go through survey flow
          navigate(`/signup-survey?${surveyParams.toString()}`, { replace: true });
        } else {
          // Regular Pro user visiting upgrade page, redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
        return;
      }
    }
  }, [isAuthenticated, user, authLoading, isStripeSuccessRedirect, paymentIntentFromUrl, customerIdFromQuery, emailFromQuery, navigate]);

  // 🚨 FIX: Additional fallback for any success-related parameters
  useEffect(() => {
    // Look for any indication this might be a post-payment redirect
    const hasSuccessIndicators = searchParams.get('purchase_success') === 'true' || 
                                 searchParams.get('upgrade_success') === 'true' ||
                                 (paymentIntentFromUrl && emailFromQuery);
    
    if (hasSuccessIndicators && emailFromQuery && !isStripeSuccessRedirect) {
      console.log('[UpgradeToProPage] Detected success parameters, redirecting to survey');
      
      const surveyParams = new URLSearchParams();
      surveyParams.append('email', emailFromQuery);
      if (searchParams.get('name')) surveyParams.append('name', searchParams.get('name'));
      if (customerIdFromQuery) surveyParams.append('customerId', customerIdFromQuery);
      if (paymentMethodIdFromQuery) surveyParams.append('paymentMethodId', paymentMethodIdFromQuery);
      if (last4FromQuery) surveyParams.append('last4', last4FromQuery);
      surveyParams.append('purchase_success', 'true');
      surveyParams.append('source_flow', sourceFlowFromQuery || 'upgrade_page_success_fallback');
      surveyParams.append('plan_type', 'pro-plan');
      if (paymentIntentFromUrl) surveyParams.append('payment_intent', paymentIntentFromUrl);
      
      navigate(`/signup-survey?${surveyParams.toString()}`, { replace: true });
      return;
    }
  }, [searchParams, emailFromQuery, customerIdFromQuery, paymentMethodIdFromQuery, last4FromQuery, sourceFlowFromQuery, paymentIntentFromUrl, isStripeSuccessRedirect, navigate]);

  const { meta: paymentMetaFromHook, error: paymentMetaErrorFromHook, loading: paymentMetaLoading } = usePaymentReuse(
    (fromStarterSignup && customerIdFromQuery && paymentMethodIdFromQuery) ? null : tokenFromUrl,
    getAccessTokenSilently
  );
  
  const [oneClickUpgradeState, setOneClickUpgradeState] = useState({
    processing: false,
    error: null,
    success: false,
  });
  const [lastUpgradeEmail, setLastUpgradeEmail] = useState(null); // Track email used for upgrade
  const [showExitModal, setShowExitModal] = useState(false);
  const [showOneClickConfirmModal, setShowOneClickConfirmModal] = useState(false);
  
  // Use useRef to track if upgrade is in progress to prevent race conditions
  const upgradeInProgressRef = useRef(false);


  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tokenConsumed, setTokenConsumed] = useState(false);
  const [tokenConsumeErrorState, setTokenConsumeErrorState] = useState(null);
  const [showFullCheckoutForm, setShowFullCheckoutForm] = useState(false); // This state is key
  const [initialEmailForForm, setInitialEmailForForm] = useState('');
  const [initialNameForForm, setInitialNameForForm] = useState('');
  
  const benefitsRef = useRef(null);
  const valueRef = useRef(null);
  const proofRef = useRef(null);
  const faqRef = useRef(null);
  const checkoutFormRef = useRef(null); 
  const comparisonRef = useRef(null); 
  const starterResourcesRef = useRef(null);

  const [spotsLeft, setSpotsLeft] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [ctaButtonText, setCtaButtonText] = useState("Upgrade to Pro Now");
  const [showStickyRibbon, setShowStickyRibbon] = useState(false);

  const [firstBotName, setFirstBotName] = useState('');
  const [primaryAsset, setPrimaryAsset] = useState('');

  const [showStarterToast, setShowStarterToast] = useState(true);

  // ADDED: State for pricing data
  const [pricingInfo, setPricingInfo] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [promoCodeFromUrl, setPromoCodeFromUrl] = useState(searchParams.get('promo') || '');
  const [pricingError, setPricingError] = useState(null); // For displaying pricing fetch errors
  
  // ADDED: State for already subscribed modal
  const [showAlreadySubscribedModal, setShowAlreadySubscribedModal] = useState(false);
  const [alreadySubscribedEmail, setAlreadySubscribedEmail] = useState("");
  const [alreadySubscribedCustomerId, setAlreadySubscribedCustomerId] = useState("");

  // ADDED: Add bootcamp ref
  const bootcampRef = useRef(null);

  const {
    startCheckout: startProCheckout,
    processing: checkoutRedirecting,
    checkoutError,
    setCheckoutError,
    catalogLoading: checkoutCatalogLoading,
    catalogError: checkoutCatalogError,
  } = useCheckoutRedirect('upgrade_to_pro');

  // Helper to format pricing for display
  const addDollarSign = (str) => {
    if (!str || str === 'TBD') return str;
    const withDollar = str.startsWith('$') ? str : `$${str}`;
    return withDollar.replace('/month', '/mo');
  };

  // Get formatted pricing details for FAQ
  const getFormattedPricing = () => {
    if (!pricingInfo?.products) {
      return { upfront: 'TBD', monthly: 'TBD' };
    }
    
    const upfrontProduct = pricingInfo.products.find(p => p.slug === 'pro-plan-upfront');
    const monthlyProduct = pricingInfo.products.find(p => p.slug === 'pro-plan');
    
    const upfrontPrice = upfrontProduct?.discountedPrice?.formatted || upfrontProduct?.basePrice?.formatted || 'TBD';
    const monthlyPrice = monthlyProduct?.discountedPrice?.formatted || monthlyProduct?.basePrice?.formatted || 'TBD';
    
    return {
      upfront: addDollarSign(upfrontPrice),
      monthly: addDollarSign(monthlyPrice)
    };
  };

  // Create dynamic FAQs based on pricing
  const createDetailedFaqs = () => {
    const pricing = getFormattedPricing();
    
    const faqs = [
      { 
        question: "What's the price for Pro?", 
        answer: `${pricing.upfront} today, then ${pricing.monthly.replace('/mo', '')}/mo. Cancel anytime. You get immediate access to all benefits upon signup.`
      },
      { 
        question: "How is Pro different from the Starter plan?", 
        answer: planFeaturesData.meta.proPlanShortDescription 
      },
      {
        question: "Can I cancel anytime?",
        answer: "Absolutely. Cancel anytime from your account to stop future monthly billing. No call is required."
      },
      { 
        question: "What if I need help setting up my Pro account or strategies?", 
        answer: "Pro members receive priority support and two 30-minute 1-on-1 calls per month which can be used for setup, strategy discussion, debugging, or any other assistance you need to maximize your Pro benefits." 
      },
      { 
        question: "Are new features and strategies added to the Pro plan regularly?", 
        answer: "Yes, your Pro subscription ensures continuous access to ongoing updates, new Pro-level strategies, enhanced AI capabilities, and new features as they are released. Our goal is to continuously increase the value for our Pro members." 
      },
      { 
        question: "What happens to my Starter bots if I cancel Pro?", 
        answer: "They keep running. You lose future access to Pro-only tools, live sessions and new strategies, but existing Starter bots stay active." 
      }
    ];
    
    return faqs;
  };

  const detailedFaqs = createDetailedFaqs();
  const cancelFaqIndex = detailedFaqs.findIndex(faq => faq.question === "Can I cancel anytime?");
  const defaultExpandedFaqKey = cancelFaqIndex !== -1 ? cancelFaqIndex.toString() : undefined;

  const checkoutDisabled = checkoutRedirecting || checkoutCatalogLoading;
  const primaryCtaLabel = checkoutRedirecting ? 'Loading checkout…' : 'Unlock Pro Now';
  const checkoutStatusMessage = checkoutError || checkoutCatalogError?.message || null;
  const canDismissCheckoutStatus = Boolean(checkoutError);
  const handleDismissCheckoutError = useCallback(() => {
    if (canDismissCheckoutStatus) {
      setCheckoutError(null);
    }
  }, [canDismissCheckoutStatus, setCheckoutError]);

  // Debug logging for URL parameter
  console.log('UpgradeToProPage - URL params:', {
    from_starter_signup: searchParams.get('from_starter_signup'),
    fromStarterSignup: fromStarterSignup,
    customerIdFromQuery: customerIdFromQuery,
    paymentMethodIdFromQuery: paymentMethodIdFromQuery,
    emailFromQuery: emailFromQuery,
    last4FromQuery: last4FromQuery,
    currentURL: window.location.href
  });

  const scrollToRef = (ref) => {
    if (ref && ref.current) {
      const offsetTop = ref.current.offsetTop - 120; // Account for sticky ribbon + jump nav
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const handleCountdownComplete = () => {
    console.log("Countdown timer in ProPageContent has completed.");
  };

  const handleSkipProUpgrade = () => {
    // Fire existing skip upsell event
    pushEvent({
        event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_SKIP_UPSELL_CLICKED, 
        source: location.pathname,
        userEmail: initialEmailForForm, 
    });
    
    // 🎯 NEW: Fire generic skip to starter plan event for funnel analytics
    pushEvent({
        event: ANALYTICS_EVENTS.SKIP_TO_STARTER_PLAN,
        source: location.pathname,
        userEmail: initialEmailForForm,
        customerId: customerIdFromQuery,
        upgrade_context: 'pro_upsell_page'
    });
    
    navigate('/dashboard'); 
  };

  const beginProCheckoutFlow = useCallback(
    (ctaLocation = 'upgrade_to_pro', overrides = {}) => {
      const contactEmail = overrides.email || initialEmailForForm || emailFromQuery || '';
      const contactName = overrides.name || initialNameForForm || '';
      const contactPhone = overrides.phone || '';

      return startProCheckout({
        products: [
          { productSlug: 'pro-plan' },
          { productSlug: 'ai-bootcamp', planKey: 'bootcamp-upfront' },
        ],
        flowContext: 'upgrade_to_pro',
        metadata: {
          origin: {
            flow: 'upgrade_to_pro',
            ctaLocation,
            sourcePath: location.pathname,
          },
        },
        contact: {
          email: contactEmail,
          name: contactName,
          phone: contactPhone,
        },
      });
    },
    [emailFromQuery, initialEmailForForm, initialNameForForm, location.pathname, startProCheckout],
  );

  useEffect(() => {
    let prefilledEmail = '';
    let prefilledName = '';

    if (fromStarterSignup && emailFromQuery) {
      prefilledEmail = emailFromQuery;
      // Name might not be passed from starter checkout, or might be part of user object if they logged in fast
    } else if (emailFromQuery) { // Handles email from other flows like direct pro-success redirect
      prefilledEmail = emailFromQuery;
    } else if (isAuthenticated && user?.email) {
      prefilledEmail = user.email;
    } else if (location.state?.email) {
      prefilledEmail = location.state.email;
    }
    
    if (isAuthenticated && user?.name && user.name !== "New User") {
      prefilledName = user.name;
    } else if (location.state?.name) {
      prefilledName = location.state.name;
    }
    
    setInitialEmailForForm(prefilledEmail || '');
    setInitialNameForForm(prefilledName);
    // setEmail(prefilledEmail); // Keep existing setEmail for direct form use
    // setName(prefilledName);   // Keep existing setName for direct form use

    if (fromStarterSignup && prefilledEmail) {
      pushEvent({
        event: ANALYTICS_EVENTS.STARTER_PLAN_SUCCESS_PASSWORD_EMAIL_INFO,
        source: location.pathname,
        userEmail: prefilledEmail,
        customerId: customerIdFromQuery,
        flow: 'direct_to_upsell_page'
      });
    }


    // Initialize random values for social proof
    setSpotsLeft(Math.floor(Math.random() * (12 - 5 + 1)) + 5); // Random between 5 and 12
    setViewerCount(Math.floor(Math.random() * (12 - 5 + 1)) + 5); // Random between 5 and 12, changed from 40-150

    // Analytics for sections viewed (simplified: on page load) - REMOVED FOR TASK 8
    // pushEvent({ event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_GUARANTEE_VIEWED, source: location.pathname, page_title: 'UpgradeToProPage' });
    // pushEvent({ event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_COMPARE_VIEWED, source: location.pathname, page_title: 'UpgradeToProPage' });
    
    // Task 8: Initial page view event (if desired, can be more specific)
    // This event is pushed regardless of one-click or full form flow.
    pushEvent({ 
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_PAGE_VIEW,
      source: location.pathname, 
      page_title: 'UpgradeToProPage',
      has_upgrade_token: !!tokenFromUrl,
      from_starter_signup: fromStarterSignup, // Track if coming from starter signup
      source_flow: sourceFlowFromQuery, // Track specific flow
      email: prefilledEmail, // Add email if available
      customerId: customerIdFromQuery // Add customerId if available
    });


    // Timer for sticky ribbon
    const ribbonTimer = setTimeout(() => {
      setShowStickyRibbon(true);
    }, 15000); 

    // Exit-Intent Logic (Task 7)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 || e.clientX <= 0 || (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight)) {
        const exitModalShown = sessionStorage.getItem('exitModalShownProPage');
        if (!exitModalShown) {
          // Check if not on a small screen (simple check, can be more robust)
          if (window.innerWidth > 768) {
            setShowExitModal(true);
            sessionStorage.setItem('exitModalShownProPage', 'true');
            pushEvent({ event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_EXIT_INTENT_MODAL_VIEWED, source: location.pathname }); 
          }
        }
      }
    };
    document.documentElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(ribbonTimer);
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
    };

  }, [location.state, isAuthenticated, user, location.pathname, tokenFromUrl, fromStarterSignup, emailFromQuery, customerIdFromQuery, paymentMethodIdFromQuery, sourceFlowFromQuery, setSearchParams]); // Added setSearchParams

  // 🚨 UNIFIED FLOW FIX: Create complete paymentMeta from URL parameters when coming from starter signup
  // Also try to restore from session storage if URL params are lost
  let paymentMeta = fromStarterSignup && customerIdFromQuery && paymentMethodIdFromQuery && emailFromQuery
    ? { 
        email: emailFromQuery, 
        customerId: customerIdFromQuery, 
        paymentMethodId: paymentMethodIdFromQuery, 
        last4: last4FromQuery || '****',
        name: searchParams.get('name') || '',
        source: 'starter_signup_redirect'
      }
    : paymentMetaFromHook;

  // 🚨 FIX: Fallback to session storage if URL parameters are lost (e.g., after 3DS redirect)
  if (!paymentMeta && fromStarterSignup && customerIdFromQuery && !paymentMethodIdFromQuery) {
    try {
      const backupPaymentMeta = sessionStorage.getItem('oneClickUpgradeBackup');
      if (backupPaymentMeta) {
        const parsed = JSON.parse(backupPaymentMeta);
        // Only use backup if it's recent (within 10 minutes) and matches current customer
        if (parsed.customerId === customerIdFromQuery && (Date.now() - parsed.timestamp) < 10 * 60 * 1000) {
          paymentMeta = {
            email: parsed.email,
            customerId: parsed.customerId,
            paymentMethodId: parsed.paymentMethodId,
            last4: parsed.last4,
            name: parsed.name,
            source: 'session_storage_backup'
          };
          console.log('[UpgradeToProPage] Restored payment meta from session storage backup', paymentMeta);
        }
      }
    } catch (err) {
      console.warn('[UpgradeToProPage] Failed to restore payment meta from session storage:', err);
    }
  }

  // 🚨 FIX: Store payment meta in session storage as backup for one-click upgrade resilience
  useEffect(() => {
    if (paymentMeta && paymentMeta.paymentMethodId && fromStarterSignup) {
      const backupPaymentMeta = {
        paymentMethodId: paymentMeta.paymentMethodId,
        customerId: paymentMeta.customerId,
        email: paymentMeta.email,
        last4: paymentMeta.last4,
        name: paymentMeta.name,
        source: paymentMeta.source,
        timestamp: Date.now()
      };
      sessionStorage.setItem('oneClickUpgradeBackup', JSON.stringify(backupPaymentMeta));
      console.log('[UpgradeToProPage] Stored payment meta backup for one-click upgrade resilience');
    }
  }, [paymentMeta, fromStarterSignup]);

  // Debug logging for paymentMeta
  console.log('UpgradeToProPage - paymentMeta:', {
    paymentMeta: paymentMeta,
    paymentMetaFromHook: paymentMetaFromHook,
    condition: {
      fromStarterSignup,
      customerIdFromQuery: !!customerIdFromQuery,
      paymentMethodIdFromQuery: !!paymentMethodIdFromQuery,
      emailFromQuery: !!emailFromQuery
    }
  });

  // Define performOneClickUpgrade BEFORE the useEffect that uses it
  const performOneClickUpgrade = useCallback(async () => {
    if (upgradeInProgressRef.current) {
      console.log('[UpgradeToProPage] One-click upgrade already in progress, skipping duplicate attempt');
      return;
    }

    const emailForUpgrade = paymentMeta?.email?.trim() || initialEmailForForm?.trim();
    const contactName = paymentMeta?.name || initialNameForForm || '';
    const contactPhone = paymentMeta?.phone || '';

    if (!emailForUpgrade) {
      setOneClickUpgradeState({ processing: false, error: 'A valid email is required to continue.', success: false });
      return;
    }

    upgradeInProgressRef.current = true;
    setOneClickUpgradeState({ processing: true, error: null, success: false });
    setLastUpgradeEmail(emailForUpgrade);

    pushEvent({
      event: ANALYTICS_EVENTS.ONE_CLICK_UPGRADE_PRO_ATTEMPT,
      source: location.pathname,
      customerId: paymentMeta?.customerId,
    });

    try {
      await beginProCheckoutFlow('one_click_redirect', {
        email: emailForUpgrade,
        name: contactName,
        phone: contactPhone,
      });
      setShowOneClickConfirmModal(false);
    } catch (error) {
      console.error('[UpgradeToProPage] Failed to redirect to checkout for one-click path', error);
      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to open checkout right now. Please try again.';
      setOneClickUpgradeState({ processing: false, error: errorMessage, success: false });
      upgradeInProgressRef.current = false;
      pushEvent({
        event: ANALYTICS_EVENTS.ONE_CLICK_UPGRADE_PRO_ERROR,
        source: location.pathname,
        customerId: paymentMeta?.customerId,
        errorMessage,
        errorType: error?.type || 'redirect_error',
      });
      return;
    }

    upgradeInProgressRef.current = false;
    setOneClickUpgradeState({ processing: false, error: null, success: false });
  }, [beginProCheckoutFlow, initialEmailForForm, initialNameForForm, location.pathname, paymentMeta, pushEvent, setShowOneClickConfirmModal]);

  // REMOVED: Auto-trigger one-click upgrade
  // The auto-upgrade behavior has been removed to ensure users must explicitly:
  // 1. Click to open the one-click upgrade modal
  // 2. Click to confirm the upgrade
  // This provides better user control and prevents accidental upgrades.
  const [autoUpgradeTriggered, setAutoUpgradeTriggered] = useState(false);
  
  // This useEffect has been removed - no more auto-triggering upgrades

  const handleOneClickUpgradeAttempt = () => {
    if (!paymentMeta || !paymentMeta.email) {
      // Missing meta or email: fallback to full checkout modal silently
      beginProCheckoutFlow('one_click_missing_metadata', {
        email: initialEmailForForm,
        name: initialNameForForm,
      });
      return;
    }
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_ONE_CLICK_CONFIRMATION_MODAL_VIEWED,
      source: location.pathname,
      customerId: paymentMeta.customerId,
    });
    setShowOneClickConfirmModal(true);
  };

  const handleConfirmOneClickUpgrade = () => {
    // setShowOneClickConfirmModal(false); // CRITICAL: This line is removed/commented. The modal does NOT close here.
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_ONE_CLICK_CONFIRMATION_MODAL_CONFIRMED,
      source: location.pathname,
      customerId: paymentMeta?.customerId,
    });
    performOneClickUpgrade(); // This will set oneClickUpgradeState.processing to true, triggering modal content update.
  };

  const handleCancelOneClickUpgrade = () => {
    setShowOneClickConfirmModal(false); // Modal closes on explicit cancel.
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_ONE_CLICK_CONFIRMATION_MODAL_CANCELLED,
      source: location.pathname,
      customerId: paymentMeta?.customerId,
    });
    // If there was an error displayed in the modal, reset it when closing.
    if (oneClickUpgradeState.error) {
      setOneClickUpgradeState(prevState => ({ ...prevState, error: null, processing: false }));
    }
  };




  // Only attempt to consume the token once per navigation
  useEffect(() => {
    if (
      tokenFromUrl &&
      !tokenConsumed &&
      !paymentMeta &&
      !paymentMetaErrorFromHook
    ) {
      setTokenConsumed(true);
      // usePaymentReuse hook will handle the fetch, so nothing else needed here
    }
  }, [tokenFromUrl, tokenConsumed, paymentMeta, paymentMetaErrorFromHook]);

  // Handle token expiration error (410 Gone)
  useEffect(() => {
    if (paymentMetaErrorFromHook && paymentMetaErrorFromHook.message && paymentMetaErrorFromHook.message.toLowerCase().includes('expired')) {
      setTokenConsumeErrorState('Your saved payment link has expired or was already used. Please enter your card below to upgrade.');
    }
  }, [paymentMetaErrorFromHook]);

  // After successful one-click upgrade, expire the token and navigate
  useEffect(() => {
    const expireToken = async () => {
      if (tokenFromUrl && oneClickUpgradeState.success) {
        try {
          const authToken = await getAccessTokenSilently();
          await fetch(`${API_URL}/stripe/expire-temp-payment-meta`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
            body: JSON.stringify({ token: tokenFromUrl }),
          });
        } catch (err) {
          console.warn('Failed to expire temp payment token:', err);
        }
      }
    };
    expireToken();
    
    // 🚨 FIX: Don't auto-redirect to survey after one-click upgrade success
    // Instead, let the user stay on the page and see the success state
    if (oneClickUpgradeState.success === true) {
      console.log('[UpgradeToProPage] One-click upgrade success detected, keeping modal open to show success state');
      console.log('[UpgradeToProPage] Current modal state showOneClickConfirmModal:', showOneClickConfirmModal);
      // Keep the modal open to show the success state - don't close it
      // The user will manually continue via the success modal's "Continue to Onboarding" button
    }
  }, [tokenFromUrl, oneClickUpgradeState.success, API_URL, navigate, paymentMeta, user, initialEmailForForm, lastUpgradeEmail, getAccessTokenSilently]);

  useLayoutEffect(() => {
    document.body.classList.add('no-header');
    return () => {
      document.body.classList.remove('no-header');
    };
  }, []);

  // --- New: Sticky secondary CTA handler ---
  const handleStayOnStarterClick = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_SKIP_UPSELL_CLICKED, 
      source: location.pathname,
      userEmail: initialEmailForForm, 
    });
    // 🚨 UNIFIED FLOW FIX: Continue to survey instead of set-password
    // Starter users who skip the Pro upgrade should continue to survey, then onboarding
    const surveyParams = new URLSearchParams();
    if (initialEmailForForm) surveyParams.append('email', initialEmailForForm);
    if (searchParams.get('name')) surveyParams.append('name', searchParams.get('name'));
    if (searchParams.get('customerId')) surveyParams.append('customerId', searchParams.get('customerId'));
    
    // 🚨 FIX: Preserve payment method info and plan type for proper parameter forwarding
    if (paymentMeta?.paymentMethodId) surveyParams.append('paymentMethodId', paymentMeta.paymentMethodId);
    if (paymentMeta?.last4) surveyParams.append('last4', paymentMeta.last4);
    // Only add paymentMeta name if we don't already have a name from searchParams
    if (paymentMeta?.name && !searchParams.get('name')) surveyParams.append('name', paymentMeta.name);
    
    surveyParams.append('from_starter_signup', 'true');
    surveyParams.append('plan_type', 'starter'); // 🚨 FIX: Add plan_type for proper tracking
    surveyParams.append('skipped_pro_upgrade', 'true');
    surveyParams.append('source_flow', 'upgrade_to_pro_skip');
    
    navigate(`/signup-survey?${surveyParams.toString()}`);
  };

  // --- New: Pay over time link (UI only) ---
  // TODO enable when SplitPay live
  const payOverTimeLink = null;

  // --- FIX: Reset showFullCheckoutForm if one-click meta is present from URL ---
  useEffect(() => {
    if (
      fromStarterSignup &&
      customerIdFromQuery &&
      paymentMethodIdFromQuery
    ) {
      // If we have the parameters for one-click from starter signup,
      // ensure the full form is not shown by default.
      setShowFullCheckoutForm(false);
    }
  }, [fromStarterSignup, customerIdFromQuery, paymentMethodIdFromQuery]);
  
  // Determine if we are in a direct one-click flow via URL parameters
  // Note: last4 is optional, so we don't require it for direct flow detection
  const isDirectOneClickFlow = fromStarterSignup && customerIdFromQuery && paymentMethodIdFromQuery;

  // Effective errors to pass to CheckoutSection
  const effectivePaymentMetaError = isDirectOneClickFlow ? null : paymentMetaErrorFromHook;
  const effectiveTokenConsumeError = isDirectOneClickFlow ? null : tokenConsumeErrorState;

  // NEW: Determine if "Stay on Starter" CTAs should be shown (only after starter signup)
  const showStayOnStarterCTAs = fromStarterSignup;

  // Modify isOneClickPathAvailable condition to require paymentMeta.email to avoid empty email error
  const isOneClickPathAvailable =
    paymentMeta &&
    paymentMeta.customerId &&
    paymentMeta.paymentMethodId &&
    paymentMeta.last4 &&
    paymentMeta.email &&
    !effectivePaymentMetaError &&
    !effectiveTokenConsumeError &&
    !oneClickUpgradeState.success;

  // 🚨 DEBUG: Log one-click availability for starter signup flow
  useEffect(() => {
    if (fromStarterSignup) {
      console.log('🔍 [UpgradeToProPage] One-click availability check:', {
        paymentMeta,
        isOneClickPathAvailable,
        effectivePaymentMetaError,
        effectiveTokenConsumeError,
        oneClickUpgradeState,
        showFullCheckoutForm,
        // Detailed breakdown of isOneClickPathAvailable conditions:
        conditions: {
          hasPaymentMeta: !!paymentMeta,
          hasCustomerId: !!(paymentMeta && paymentMeta.customerId),
          hasPaymentMethodId: !!(paymentMeta && paymentMeta.paymentMethodId),
          hasLast4: !!(paymentMeta && paymentMeta.last4),
          hasEmail: !!(paymentMeta && paymentMeta.email),
          noPaymentMetaError: !effectivePaymentMetaError,
          noTokenConsumeError: !effectiveTokenConsumeError,
          notAlreadySuccessful: !oneClickUpgradeState.success
        }
      });
    }
  }, [fromStarterSignup, paymentMeta, isOneClickPathAvailable, effectivePaymentMetaError, effectiveTokenConsumeError, oneClickUpgradeState, showFullCheckoutForm]);

  // Declare early to avoid temporal dead-zone when referenced in hooks below
  let priceBlurbRibbon = '';

  // Unified handler for "Unlock Pro" buttons
  const handleUnlockProFlow = () => {
    if (checkoutDisabled) {
      console.log('[UpgradeToProPage] Checkout redirect still preparing, ignoring CTA click.');
      return;
    }

    console.log('🚀 [UpgradeToProPage] handleUnlockProFlow called:', {
      isOneClickPathAvailable,
      showFullCheckoutForm,
      paymentMeta: paymentMeta ? { ...paymentMeta } : null,
      willUseOneClick: isOneClickPathAvailable && !showFullCheckoutForm
    });

    pushEvent({
      event: ANALYTICS_EVENTS.UPGRADE_PAGE_PRIMARY_CTA_CLICKED,
      source: location.pathname,
      cta_location: 'generic_unlock_pro_button',
      cta_type: 'direct_upgrade',
      button_text: 'Unlock Pro Now',
      one_click_available: !!isOneClickPathAvailable,
      showing_full_form: showFullCheckoutForm
    });

    pushEvent({ 
      event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_CTA_CLICKED,
      source: location.pathname,
      cta_location: 'generic_unlock_pro_button', 
      one_click_available: !!isOneClickPathAvailable,
      showing_full_form: showFullCheckoutForm, 
    });

    if (isOneClickPathAvailable && !showFullCheckoutForm) {
      console.log('🎯 [UpgradeToProPage] Using one-click upgrade path');
      handleOneClickUpgradeAttempt(); 
    } else {
      console.log('💳 [UpgradeToProPage] Redirecting to unified checkout');
      setCheckoutError(null);
      beginProCheckoutFlow('generic_unlock_pro_button', {
        email: initialEmailForForm,
        name: initialNameForForm,
      });
    }
  };



  // Define modal content variables here, ensuring they are in scope for the main return
  const modalHandleClose = oneClickUpgradeState.processing ? () => {} : handleCancelOneClickUpgrade;

  // --- derive dynamic pricing strings for one-click modal ---
  // Fetch detailed price objects once so we can reference them in modal and below UI elements
  function getProductDisplayDetailsForPage(slug) {
    if (!pricingInfo || !pricingInfo.products || !Array.isArray(pricingInfo.products)) {
      return {
        currentFormatted: 'TBD',
        originalFormatted: 'TBD',
        isDiscounted: false,
        discountPercent: 0,
        discountAmount: 0,
        discountEndDate: null,
      };
    }

    const product = pricingInfo.products.find(p => p.slug === slug);
    if (!product) {
      return {
        currentFormatted: 'TBD',
        originalFormatted: 'TBD',
        isDiscounted: false,
        discountPercent: 0,
        discountAmount: 0,
        discountEndDate: null,
      };
    }

    const currentPrice = product.discountedPrice || product.basePrice;
    const originalPrice = product.basePrice;
    const isDiscounted = !!product.discountedPrice;
    const discountPercent = isDiscounted ? Math.round(((originalPrice.amount - currentPrice.amount) / originalPrice.amount) * 100) : 0;
    const discountAmount = isDiscounted ? originalPrice.amount - currentPrice.amount : 0;
    const discountEndDate = product.discountEndDate ? new Date(product.discountEndDate) : null;

    return {
      currentFormatted: addDollarSign(currentPrice.formatted),
      originalFormatted: addDollarSign(originalPrice.formatted),
      isDiscounted,
      discountPercent,
      discountAmount,
      discountEndDate,
    };
  }

  const upfrontDetailsPage = getProductDisplayDetailsForPage('pro-plan-upfront');
  const monthlyDetailsPage = getProductDisplayDetailsForPage('pro-plan');
  const monthlyPriceStringPage = monthlyDetailsPage.currentFormatted || 'TBD';

  const upfrontProductInfo = pricingInfo?.products?.find(p => p.slug === 'pro-plan-upfront');
  const monthlyProductInfo = pricingInfo?.products?.find(p => p.slug === 'pro-plan');

  const upfrontPriceCurrent = upfrontProductInfo?.discountedPrice?.formatted || upfrontProductInfo?.basePrice?.formatted || 'TBD';
  const upfrontPriceOriginal = upfrontProductInfo?.basePrice?.formatted || 'TBD';
  const monthlyPriceCurrent = monthlyProductInfo?.discountedPrice?.formatted || monthlyProductInfo?.basePrice?.formatted || 'TBD';
  
  const isUpfrontDiscounted = upfrontProductInfo?.discountedPrice && upfrontProductInfo.discountedPrice.amount < upfrontProductInfo.basePrice.amount;

  // Calculate savings for hero section
  const upfrontOriginalAmount = upfrontProductInfo?.basePrice?.amount || 0;
  const upfrontCurrentAmount = upfrontProductInfo?.discountedPrice?.amount || upfrontProductInfo?.basePrice?.amount || 0;
  const monthlyOriginalAmount = monthlyProductInfo?.basePrice?.amount || 0;
  const monthlyCurrentAmount = monthlyProductInfo?.discountedPrice?.amount || monthlyProductInfo?.basePrice?.amount || 0;
  
  const totalSavingsAmount = (upfrontOriginalAmount - upfrontCurrentAmount) + (monthlyOriginalAmount - monthlyCurrentAmount);
  const savingsFormatted = totalSavingsAmount > 0 ? `$${(totalSavingsAmount / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}` : '';

  // Calculate total initial charge for the button
  const upfrontAmountCents = upfrontProductInfo?.discountedPrice?.amount || upfrontProductInfo?.basePrice?.amount || 0;
  const monthlyAmountCents = monthlyProductInfo?.discountedPrice?.amount || monthlyProductInfo?.basePrice?.amount || 0;
  const totalInitialCharge = (upfrontAmountCents + monthlyAmountCents) / 100;
  const totalInitialChargeFormatted = `$${totalInitialCharge.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  let modalTitle = "Confirm Your Pro Upgrade";
  let modalBody = (
    <>
      <div className="upgrade-to-pro-page-modal-pricing">
        <div className="upgrade-to-pro-page-modal-pricing-current">
          {upfrontDetailsPage.currentFormatted} + ${monthlyDetailsPage.currentFormatted.replace('$', '').replace('/month', '').replace('/mo', '')}/mo
        </div>
        {upfrontDetailsPage.isDiscounted && upfrontDetailsPage.originalFormatted !== 'TBD' && (
          <div className="upgrade-to-pro-page-modal-pricing-was">
            <span className="upgrade-to-pro-page-modal-strikethrough">
              Was {upfrontDetailsPage.originalFormatted} + ${monthlyDetailsPage.originalFormatted?.replace('$', '').replace('/month', '').replace('/mo', '') || monthlyDetailsPage.currentFormatted.replace('$', '').replace('/month', '').replace('/mo', '')}/mo
            </span>
            {savingsFormatted && (
              <span className="upgrade-to-pro-page-modal-pricing-savings-inline">
                Save {savingsFormatted}
              </span>
            )}
          </div>
        )}
      </div>
      
      <div className="upgrade-to-pro-page-modal-charge-summary">
        <div className="upgrade-to-pro-page-modal-charge-today">
          <strong>{totalInitialChargeFormatted}</strong> due today — you'll choose your payment method on the next screen
        </div>
        {monthlyPriceCurrent !== 'TBD' && (
          <div className="upgrade-to-pro-page-modal-recurring">
            Then ${monthlyPriceCurrent.replace('$', '').replace('/month', '').replace('/mo', '')}/month • Cancel anytime
          </div>
        )}
      </div>

    </>
  );

  // Define modalActionButton reactively based on oneClickUpgradeState.processing
  let modalActionButton = {
    text: oneClickUpgradeState.processing ? (
      <>
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Redirecting…
      </>
    ) : `Continue to checkout`,
    onClick: handleConfirmOneClickUpgrade,
    variant: "primary", // Use "primary" for custom styling with className
    className: "upgrade-to-pro-page-hero-main-cta", // Apply orange button style
    disabled: oneClickUpgradeState.processing,
  };

  let modalCloseButtonText = null;
  let modalSecondaryActionButton = null;

  if (oneClickUpgradeState.processing) {
    modalTitle = "Processing Your Upgrade...";
    modalBody = (
      <div className="text-center">
        <span className="spinner-border spinner-border-lg mb-2" role="status" aria-hidden="true"></span>
        <p className="mt-2">Please wait while we securely process your Pro upgrade. Do not close this window.</p>
      </div>
    );
    // modalActionButton is already correctly defined above for the processing state.
    // We only need to adjust other modal elements.
    modalCloseButtonText = null; // No explicit cancel during processing
    modalSecondaryActionButton = null; // Hide secondary button during processing
  } else if (oneClickUpgradeState.success) {
    modalTitle = "🎉 Upgrade Successful!";
    modalBody = (
      <div className="text-center">
        <div className="mb-3">
          <FaCheckCircle className="text-success" style={{ fontSize: '3rem' }} />
        </div>
        <p className="fw-bold">Welcome to Lumivest Pro!</p>
        <p>Your upgrade has been processed successfully. You now have access to all Pro features.</p>
        <p>Continue below to complete your onboarding and start using Pro features.</p>
      </div>
    );
    modalActionButton = {
      text: "Continue to Onboarding",
      variant: "success",
      onClick: () => {
        setShowOneClickConfirmModal(false);
        const emailForNav = lastUpgradeEmail || paymentMeta?.email || user?.email || initialEmailForForm;
        const customerIdForNav = paymentMeta?.customerId;
        
        // Navigate to survey first, then onboarding (unified flow)
        const surveyParams = new URLSearchParams();
        if (emailForNav) surveyParams.append('email', emailForNav);
        if (customerIdForNav) surveyParams.append('customerId', customerIdForNav);
        // 🚨 FIX: Preserve payment method ID so one-click upgrade can work if user returns
        if (paymentMeta?.paymentMethodId) surveyParams.append('paymentMethodId', paymentMeta.paymentMethodId);
        if (paymentMeta?.last4) surveyParams.append('last4', paymentMeta.last4);
        if (paymentMeta?.name) surveyParams.append('name', paymentMeta.name);
        surveyParams.append('purchase_success', 'true');
        surveyParams.append('plan_type', 'pro-plan');
        surveyParams.append('source_flow', 'upgrade_to_pro_one_click_success');
        surveyParams.append('auto_login', 'true'); // Flag for auto-login after survey
        
        navigate(`/signup-survey?${surveyParams.toString()}`);
        
        // Clean up session storage backup after successful upgrade
        sessionStorage.removeItem('oneClickUpgradeBackup');
      }
    };
    modalCloseButtonText = null; // No close button - force user to continue
    modalSecondaryActionButton = null; // No secondary button
  } else if (oneClickUpgradeState.error) {
    modalTitle = "Upgrade Error";
    modalBody = (
      <Alert variant="danger" className="text-break">
        <FaTimes className="me-2" />
        {oneClickUpgradeState.error} {/* Shows error message inside the modal. */}
      </Alert>
    );
    modalActionButton = null; // No primary action on error.
    modalCloseButtonText = "Close"; // "Close" button text for error state
    modalSecondaryActionButton = null; // Hide secondary button on error
  } else {
    // This is the initial confirmation state (no processing, no error)
    // modalTitle, modalBody, modalActionButton are already set correctly above for this state.
    modalCloseButtonText = null; // REMOVE Cancel button, as "Use different payment method" serves as alternative
    modalSecondaryActionButton = {
      text: "Use a different payment method",
      variant: "link",
      className: "upgrade-to-pro-page-use-different-payment-link", // For potential styling
      onClick: () => {
        setShowOneClickConfirmModal(false); // Close the one-click modal
        pushEvent({
          event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_CTA_CLICKED,
          source: location.pathname,
          cta_location: 'one_click_modal_use_different_payment',
          customerId: paymentMeta?.customerId,
          email: initialEmailForForm,
        });
        // Open the Stripe checkout form modal
        beginProCheckoutFlow('one_click_modal_use_different_payment', {
          email: initialEmailForForm,
          name: initialNameForForm,
        });
      }
    };
  }

  // ADDED: Function to fetch product pricing
  const fetchProductPricing = useCallback(async (promoCode) => {
    setIsLoadingPricing(true);
    setPricingError(null);
    // Define Pro product slugs here - adjust if they are different
    const proProductSlugs = 'pro-plan,pro-plan-upfront'; 
    let url = `${API_URL}/stripe/products-pricing?slugs=${proProductSlugs}`;
    if (promoCode) {
      url += `&promoCode=${encodeURIComponent(promoCode)}`;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Failed to fetch pricing details. Status: " + response.status }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      setPricingInfo(data);
      if (data.promoCodeFeedback) {
        // Optionally, display this feedback to the user (e.g., via an Alert component)
        console.warn("Promo code feedback from backend:", data.promoCodeFeedback);
        // You might want to set another state here to show this feedback in the UI
      }
      if (data.activePromotion) {
        console.log("Active promotion applied:", data.activePromotion);
        // Potentially update CTA text or UI elements to reflect discount
      }
    } catch (error) {
      console.error("Failed to fetch product pricing:", error);
      setPricingError(error.message || "Could not load pricing. Please try again.");
      // Fallback to default/hardcoded pricing display or show error message
      // For now, setting pricingInfo to a structure that indicates error or uses defaults:
      setPricingInfo({
        products: [
          { slug: 'pro-plan-upfront', basePrice: { formatted: 'TBD' }, name: 'Pro Plan Upfront Fee' },
          { slug: 'pro-plan', basePrice: { formatted: 'TBD' }, name: 'Pro Plan Monthly' }
        ],
        summary: { originalFormatted: 'Pricing unavailable' },
        error: "Could not load pricing information."
      });
    } finally {
      setIsLoadingPricing(false);
    }
  }, []); // Empty dependency array means this function is stable and won't change on re-renders
  // END ADDED: Function to fetch product pricing

  // ADDED: useEffect to fetch pricing on mount and when searchParams changes
  useEffect(() => {
    const currentUrlPromo = searchParams.get('promo') || '';
    setPromoCodeFromUrl(currentUrlPromo);
    fetchProductPricing(currentUrlPromo);
  }, [searchParams, fetchProductPricing]);
  // END ADDED: useEffect for fetching pricing

  // Modal specific calculations (These should already exist, verify they use the page-suffixed versions if needed)
  const modalUpfrontCurrentFormatted = upfrontDetailsPage.currentFormatted;

  // --- BEGIN: pricing blurb helpers for sticky ribbon & modal ---
  // Build price blurb with safe checks to avoid "was undefined"
  const upfrontOriginalPart = upfrontDetailsPage.isDiscounted && upfrontDetailsPage.originalFormatted && upfrontDetailsPage.originalFormatted !== 'TBD'
    ? ` (was ${upfrontDetailsPage.originalFormatted})` : '';
  const monthlyOriginalPart = monthlyDetailsPage.isDiscounted && monthlyDetailsPage.originalFormatted && monthlyDetailsPage.originalFormatted !== 'TBD'
    ? ` (was ${monthlyDetailsPage.originalFormatted})` : '';

  // Better format: "$1,999 + $199/mo (was $2,999 + $199/mo)"
  const currentPricing = `${upfrontDetailsPage.currentFormatted} + ${monthlyDetailsPage.currentFormatted}`;
  const originalPricing = upfrontDetailsPage.isDiscounted && upfrontDetailsPage.originalFormatted !== 'TBD' 
    ? ` (was ${upfrontDetailsPage.originalFormatted} + ${monthlyDetailsPage.originalFormatted || monthlyDetailsPage.currentFormatted})` 
    : '';
  
  priceBlurbRibbon = currentPricing + originalPricing;

  // REMOVED: ribbonPricingStructured since we removed pricing from sticky ribbon to save mobile space
  // --- END: pricing blurb helpers ---

  // Early return for loading states - ADD isLoadingPricing here
  if (authLoading || (tokenFromUrl && !fromStarterSignup && paymentMetaLoading) || isLoadingPricing) {
    return (
      <Container className="text-center py-5 upgrade-to-pro-page">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading Pro Upgrade Details...</p> {/* Updated message */}
      </Container>
    );
  }

  // Handle one-click upgrade flow or token errors
  if (tokenFromUrl && !fromStarterSignup) { // Ensure this block doesn't run for from_starter_signup flow if it relies on tokenFromUrl specific logic
    // This block handles the UI for one-click states (processing, success, error)
    // It does NOT automatically trigger the upgrade anymore.
    if (paymentMetaLoading) {
      return (
        <Container className="text-center py-5 upgrade-to-pro-page">
          <Spinner animation="border" /> <p>Loading your payment information...</p>
        </Container>
      );
    }
    // If token is invalid/expired, show a message and fallback to manual form
    // Use tokenConsumeErrorState here
    if (paymentMetaErrorFromHook && tokenConsumeErrorState) {
      return (
        <Container className="text-center py-5 upgrade-to-pro-page">
          <Alert variant="warning">
            {tokenConsumeErrorState}
          </Alert>
          {/* Fallback to manual form will be rendered by the main return block */}
        </Container>
      );
    }

    // If one-click is processing or has succeeded, show dedicated UI
    if (paymentMeta) { // Ensure paymentMeta is loaded before checking oneClickUpgradeState
      if (oneClickUpgradeState.processing) {
        return (
          <Container className="text-center py-5 upgrade-to-pro-page">
            <Spinner animation="border" /> <p>Processing your Pro upgrade securely...</p>
          </Container>
        );
      }
      // If there's an error from one-click, it will be shown near the one-click button or form.
    }
  }

  // --- Pro Success Stories Cards (verified customer journeys) ---
const proCaseStudiesData = [
  {
    title: "Andrew (\"Rockers\") Spun Up 5 Bots in a Single Weekend",
    body: "\"Upgraded on Friday, had five paper-trading bots running by Sunday night. The Tuesday Live-Build feedback turned ideas into code fast — no manual scripting.\" – Andrew M., Pro member since 2024",
    img: "/images/UpgradeToPro/rockers-success-story.png",
    alt: "Multiple trading bots running on an ultra-wide screen"
  },
  {
    title: "Worth's UVXY Strategy Cut Stress and Boosted Consistency",
    body: "Worth leveraged Tuesday sessions to turn a volatile UVXY idea into an algorithm that trades hands-free while he's in client sessions. He now tracks performance with daily BotSpot reports.*",
    img: "/images/UpgradeToPro/worth-success-story.png",
    alt: "Zoom workshop with algorithm code and equity chart"
  },
  {
    title: "Ali's Options Bot Gave Him Confidence to Scale",
    body: "Ali built and back-tested an index-options bot during Pro Bootcamp. The process taught him how to tune risk and automate spreads he used to manage manually.*",
    img: "/images/UpgradeToPro/ali-success-story.png",
    alt: "Mentor and trader reviewing options performance charts"
  }
];

  return (
    <div className="upgrade-to-pro-page">
      <Helmet>
        <title>BotSpot Pro: More Bots, Backtests & Support</title>
        <meta name="description" content="Upgrade BotSpot for higher strategy revision limits, more live bots, additional backtesting capacity, and priority support." />
        <link rel="canonical" href="https://botspot.trade/upgrade-to-pro" />
      </Helmet>
      {/* Sticky Ribbon: always render, always sticky at the very top */}
      <div className="upgrade-to-pro-page-sticky-ribbon-fixed">
        <div className="upgrade-to-pro-page-sticky-ribbon-content">
          <div className="upgrade-to-pro-page-sticky-ribbon-right">
            <ProCta
              label={primaryCtaLabel}
              onClick={handleUnlockProFlow}
              className="upgrade-to-pro-page-sticky-ribbon-button"
              size="sm"
              disabled={checkoutDisabled}
            />
            {showStayOnStarterCTAs && (
              <button
                id="stay-btn-sticky-ribbon"
                className="btn btn-outline-light upgrade-to-pro-page-sticky-ribbon-secondary ms-2 btn-sm"
                onClick={handleStayOnStarterClick}
              >
                Stay on Starter →
              </button>
            )}
          </div>
        </div>
      </div>
      
      <Container className="upgrade-to-pro-page-hero-container"> 
        {/* --- Dynamic, personalized headline/sub-line --- */}
        {fromStarterSignup ? (
          <>
            <h1 className="upgrade-to-pro-page-hero-starter-headline">
              Starter unlocked <FaCheckCircle className="text-success" /> — ready for <span className="upgrade-to-pro-page-hero-highlight">FULL AI trading power?</span>
            </h1>
            <p className="upgrade-to-pro-page-hero-subtext">
              {firstBotName
                ? <>Ready to let <strong>{firstBotName}</strong> trade around the clock?</>
                : primaryAsset
                  ? <>Ready to automate your <strong>{primaryAsset}</strong> trading?</>
                  : <>Get 3 live bots included for free and access 50+ PRO strategies in minutes—no extra coding.</>
              }
            </p>
          </>
        ) : (
          <>
            <div className="text-center">
              <h1>
                Become a Lumiwealth Pro Member<br/>
                <span className="upgrade-to-pro-page-hero-highlight">Unlock UNLIMITED Trading Power</span>
              </h1>
              <p className="upgrade-to-pro-page-hero-subtext">
                Join thousands of successful traders using our advanced AI-powered platform
              </p>
            </div>
          </>
        )}

        {/* Hero CTA Section */}
        <div className="upgrade-to-pro-page-hero-cta-section">
          <div className="upgrade-to-pro-page-hero-messaging">
            <p className="upgrade-to-pro-page-hero-description">
              Cancel anytime. Get premium tools, strategies, and expert support. Elevate your trading.
            </p>
          </div>
          
          <div className="upgrade-to-pro-page-hero-cta-buttons">
            <ProCta 
              label={primaryCtaLabel} 
              onClick={handleUnlockProFlow} 
              className="upgrade-to-pro-page-hero-main-cta" 
              size="lg"
              disabled={checkoutDisabled}
            />
            {showStayOnStarterCTAs && (
              <button
                className="btn btn-outline-light upgrade-to-pro-page-hero-secondary-cta"
                onClick={handleStayOnStarterClick}
              >
                Continue with Starter
              </button>
            )}
          </div>
          {checkoutStatusMessage && (
            <Alert
              variant="danger"
              className="mt-3"
              dismissible={canDismissCheckoutStatus}
              onClose={canDismissCheckoutStatus ? handleDismissCheckoutError : undefined}
            >
              {checkoutStatusMessage}
            </Alert>
          )}
        </div>
      </Container>

      {pricingError && 
        <Container>
            <Alert variant="danger" className="my-3">Error loading pricing: {pricingError}</Alert>
        </Container>
      }

      {pricingInfo && !isLoadingPricing && (
          <ProPageContent
            benefitsRef={benefitsRef}
            valueRef={valueRef}
            proofRef={proofRef}
            faqRef={faqRef}
            checkoutFormRef={checkoutFormRef} // This ref might now be for the section, not a form
            comparisonRef={comparisonRef}
            starterResourcesRef={starterResourcesRef}
            bootcampRef={bootcampRef}
            scrollToRef={scrollToRef}
            handleCountdownComplete={handleCountdownComplete}
            logoImages={logoImages}
            viewerCount={viewerCount}
            benefitsData={benefitsData}
            spotsLeft={spotsLeft}
            ctaButtonText={"Unlock Pro"}
            openProCheckout={handleUnlockProFlow}
            openProCheckoutLabel={primaryCtaLabel}
            openProCheckoutDisabled={checkoutDisabled}
            testimonialsPro={testimonialsPro}
            proCaseStudiesData={proCaseStudiesData}
            trustBadgesData={trustBadgesData}
            detailedProFeatures={detailedProFeatures}
            comparisonFeaturesList={comparisonFeaturesList}
            featureDefinitions={planFeaturesData.featureDefinitions}
            detailedFaqs={detailedFaqs}
            defaultExpandedFaqKey={defaultExpandedFaqKey}
            onStayOnStarter={showStayOnStarterCTAs ? handleStayOnStarterClick : undefined}
            showStayOnStarter={showStayOnStarterCTAs}
            payOverTimeLink={payOverTimeLink}
            valueItems={valueItems}
            totalRetailValue={totalRetailValue}
            pricingInfo={pricingInfo}
            isLoadingPricing={isLoadingPricing}
          />
      )}

      <InfoModal
        show={showExitModal}
        handleClose={() => setShowExitModal(false)}
        title="Wait! Before You Go..."
        body={
          <>
            <p>Are you sure you want to leave this exclusive Pro offer?</p>
            <p>If you have questions or need help deciding, book a quick, no-pressure 10-minute demo call with our team. We can show you how Pro can transform your trading.</p>
          </>
        }
        actionButton={{
          text: "Book a 10-Min Demo Call",
          onClick: () => {
            pushEvent({ event: ANALYTICS_EVENTS.UPGRADE_TO_PRO_EXIT_INTENT_MODAL_CTA_CLICKED, source: location.pathname }); // Removed .name
            window.open('https://calendly.com/lumiwealth/botspot-call', '_blank'); 
            setShowExitModal(false);
          }
        }}
        closeButtonText="No Thanks, I'll Decide Later"
      />

      {/* One-Click Confirmation Modal */}
      {paymentMeta && (
        <InfoModal
          show={showOneClickConfirmModal}
          handleClose={modalHandleClose}
          title={modalTitle}
          body={modalBody}
          actionButton={modalActionButton}
          secondaryActionButton={modalSecondaryActionButton}
          closeButtonText={modalCloseButtonText}
        />
      )}

      {/* One-Click Already Subscribed Modal */}
      {showAlreadySubscribedModal && (
        <InfoModal
          show={showAlreadySubscribedModal}
          handleClose={() => {}} // Disable close functionality
          title="Already Subscribed to Pro"
          body={<>
            <p>You already have an active Pro subscription.</p>
            <p>Click below to continue to your onboarding and dashboard.</p>
          </>}
          actionButton={{
            text: "Continue to Onboarding",
            onClick: () => {
              setShowAlreadySubscribedModal(false);
              
              // 🚨 FIX: Already subscribed users should follow same flow as successful upgrade
              // Navigate to survey first, then onboarding (unified flow)
              const surveyParams = new URLSearchParams();
              if (alreadySubscribedEmail) surveyParams.append('email', alreadySubscribedEmail);
              if (alreadySubscribedCustomerId) surveyParams.append('customerId', alreadySubscribedCustomerId);
              // Preserve payment method info if available
              if (paymentMeta?.paymentMethodId) surveyParams.append('paymentMethodId', paymentMeta.paymentMethodId);
              if (paymentMeta?.last4) surveyParams.append('last4', paymentMeta.last4);
              if (paymentMeta?.name) surveyParams.append('name', paymentMeta.name);
              surveyParams.append('purchase_success', 'true'); // User is already Pro subscriber
              surveyParams.append('plan_type', 'pro-plan');
              surveyParams.append('source_flow', 'upgrade_to_pro_already_subscribed');
              surveyParams.append('auto_login', 'true');
              
              navigate(`/signup-survey?${surveyParams.toString()}`);
              
              // Clean up session storage backup
              sessionStorage.removeItem('oneClickUpgradeBackup');
            },
            variant: "success"
          }}
          closeButtonText={null} // Remove close button
          showCloseButton={false} // Hide X button
        />
      )}

      <div className="compliance-bar upgrade-to-pro-page-compliance-bar">
        <small>LumiWealth provides educational & technical tools; does not give investment advice. Past performance does not guarantee future results. Your capital is at risk. Brokerage services, if any, are provided by third-party firms.</small>
      </div>
    </div>
  );
}

export default UpgradeToProPage;
